import { Component, ElementRef, inject, ViewChild } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { firstValueFrom, forkJoin, Observable, of, Subject } from "rxjs";
import { take, takeUntil, switchMap, map, debounceTime } from "rxjs/operators";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { Title } from "@angular/platform-browser";
import { JobDetail } from "../../../models/jobDetail";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { Job } from "../../../models/job";
import { Project } from "../../../models/project";
import { NzModalService } from "ng-zorro-antd/modal";
import { JobDetailDialog } from "../../../models/jobDetailDialog";
import { StoredSettingsService } from "../../services/stored-settings.service";
import { EditJobDialogComponent } from "../dialogs/edit-job-dialog/edit-job-dialog.component";
import { EditProjectDialogComponent } from "../dialogs/edit-project-dialog/edit-project-dialog.component";
import { AddJobDialogComponent } from "../dialogs/add-job-dialog/add-job-dialog.component";
import { JobStateService } from "../model-new/job-state.store";
import { ProjectService } from "../../../shared/services/project.service";
import { FavJobsService } from "../../services/fav-jobs.service";

interface GroupInterface{
  id: string,
  name: string,
  jobs: {job__id: string, job__job_name: string}[]
}

@Component({
  selector: "app-project-new",
  templateUrl: "./project-new.component.html",
  styleUrls: ["./project-new.component.less"],
  standalone: false,
})
export class ProjectNewComponent {
  // this is what the indexChangeHandler changes, which in turn changes the job. This is to add debouncing specially when changing jobs with keypress
  private debouncedIndexChangeSubject = new Subject<{
    index: number;
    type: "all" | "fav";
  }>();
  private destroy$ = new Subject<void>();
  protected jobState = inject(JobStateService);

  private _currentJob: JobDetail;
  storageStatusString: string;
  cleanupTime: string;
  get currentJob(): JobDetail {
    return this._currentJob;
  }
  set currentJob(value: JobDetail) {
    this._currentJob = value;
    this.setParentJobString();
    this.setStorageStatus();
    this.titleService.setTitle(this.currentJob.projectName);
  }

  jobs: Job[] = [];
  // favourites live in a shared service so the model page (batch screenshots) can read them
  private favJobsService = inject(FavJobsService);
  get favJobs(): Job[] {
    return this.favJobsService.favJobs();
  }
  get hasFavJobs(): boolean {
    return this.favJobsService.hasFavJobs();
  }
  project: Project;
  specificJobView: boolean = false; // this is for cases when you are looking at specific groups using the temporary job groups
  jobsSubset: string[] = []; // ids of the jobs if this is a 'specific job view' to refresh

  protected jobGroups$: Observable<any>;

  parentJobString: string;
  parentJob: Job;

  syncSettings$ = this.storedSettingsService.synchroniseSettings;

  error: any;

  // these are all only and only for the tab bar at the bottom to becom evisible
  private resizeObserver: ResizeObserver;
  @ViewChild("anchor") set anchor(element: ElementRef) {
    if (element) {
      // The element was just created! Setup the observer.
      this.setupObserver(element.nativeElement);
      this.setupResizeObserver(element.nativeElement);
    } else {
      // The element was just destroyed by *ngIf. Clean up.
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
  }
  bottomTabsVisible = false;
  private observer: IntersectionObserver;
  protected isMobile: boolean | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cachedProjectService: CachedProjectService,
    private projectService: ProjectService,
    private titleService: Title,
    private notificationService: NzNotificationService,
    private modalService: NzModalService,
    private storedSettingsService: StoredSettingsService,
    private elementRef: ElementRef,
  ) {
    this.syncSettings$.subscribe((value) => {
      // This ensures the signal (and thus the checkbox)
      // matches the Service's state at all times
      this.jobState.synchroniseSettings.set(value);
    });
  }

  ngOnInit() {
    // this section will be run only the first time that the routeParams come.
    
    this.isMobile = /Mobile/.test(navigator.userAgent) ||
      window.innerWidth <= 600 ||
      (window.innerHeight <= 600 && window.innerWidth > window.innerHeight);

    forkJoin({
      params: this.route.params.pipe(take(1)),
      queryParams: this.route.queryParams.pipe(take(1)),
    }).subscribe(({ params, queryParams }) =>
      this.initialize(params, queryParams),
    );

    this.debouncedIndexChangeSubject
      .pipe(
        debounceTime(500), // Wait 500ms after the last keypress/click
        takeUntil(this.destroy$),
      )
      .subscribe(({ index, type }) => {
        this.onIndexChangedHandler(index, type);
      });

    this.cachedProjectService.jobDeleted
      .pipe(takeUntil(this.destroy$))
      .subscribe((deletedJobId) => this.onJobDeletedHandler(deletedJobId));
  }

  initialize(params: Params, queryParams: Params) {
    if (!("id" in params)) {
      this.notificationService.error(
        "Job ID is absent. Please check your URL.",
        "",
      );
      return;
    }

    const id = params["id"];

    // case of querParams having jobs, requiring specific jobs to be initialized instead of project
    if ("jobs" in queryParams && queryParams["jobs"].length > 0) {
      this.jobsSubset = queryParams["jobs"];
      this.loadSpecificJobs(id, this.jobsSubset);
      return;
    }

    forkJoin({
      project: this.cachedProjectService.getJobProject(id).pipe(take(1)),
      job: this.cachedProjectService.getJobDetail(id).pipe(take(1)),
    }).subscribe({
      next: ({ project, job }) => {
        this.error = null;
        // This block only runs once BOTH have returned
        this.project = project;
        this.favJobsService.setProject(project.id);
        this.jobs = project.jobs;
        // this.cdr.markForCheck();

        // tho we do this AFTER the initialization
        // make a subscription to the currentJob, so that whenever the details are updated, the currentJobEmission updates the job here in this component
        this.cachedProjectService.currentJob
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (job) => {
              // uses the set function for effects
              this.currentJob = job;
              // this.cdr.markForCheck();
            },
          });

        this.jobGroups$ = this.projectService
          .getJobGroups(this.project.id)
          .pipe(map((res) => res.data));
      },
      error: (err) => {
        this.error =
          err.error?.error || err.message || "There was an unexpected error...";
      },
    });
  }

  loadSpecificJobs(currenJob: string, jobs: string[]) {
    this.specificJobView = true;
    forkJoin({
      jobs: this.cachedProjectService.getMultipleJobData(jobs).pipe(take(1)),
      job: this.cachedProjectService.getJobDetail(currenJob),
    }).subscribe(({ job, jobs }) => {
      this.jobs = jobs;
      // tho we do this AFTER the initialization
      // make a subscription to the currentJob, so that whenever the details are updated, the currentJobEmission updates the job here in this component
      this.cachedProjectService.currentJob
        .pipe(
          takeUntil(this.destroy$),
          switchMap((job) => {
            // get the project for the corresponding job | also works in case of doesn't exist
            if (job.projectId !== this.project?.id) {
              // the way its been written causes the project to be assigned before the job is emitted to the subscription allowing the project to be assigned before project and not causing any errors
              return this.cachedProjectService.getJobProject(job.id).pipe(
                take(1),
                map((project) => {
                  this.project = project; // Assign project first
                  this.favJobsService.setProject(project.id);
                  return job; // Then return the job to the main stream
                }),
              );
            } else {
              return of(job);
            }
          }),
        )
        .subscribe((job) => {
          // uses the set function for effects
          this.currentJob = job;
          // this.cdr.markForCheck();
        });
    });
  }

  refreshProjectAndJob() {
    if (this.specificJobView) {
      // if specific jobs are the case, do the specific job loading instead of full project + job refresh
      this.loadSpecificJobs(this.currentJob.id, this.jobsSubset);
      return;
    }

    forkJoin({
      project: this.cachedProjectService
        .getProjectById(this.project.id, true)
        .pipe(take(1)),
      job: this.cachedProjectService
        .getJobDetail(this.currentJob.id, true)
        .pipe(take(1)),
    }).subscribe(({ project, job }) => {
      // This block only runs once BOTH have returned
      this.project = project;
      this.jobs = project.jobs;
      // this.cdr.markForCheck();
    });
  }

  goToProject() {
    window.open(
      "/project/" + this.currentJob.projectId + "/parameter-view",
      "_blank",
    );
  }

  updateUrl(jobid: string) {
    const currentUrl = this.router.url;
    // targetting and changing specifically and only the jobId section of the url while preserving everything else
    const re = new RegExp(/(\/projects\/)[^\/]+/);
    const nextUrl = currentUrl.replace(re, `$1${jobid}`);
    this.router.navigateByUrl(nextUrl);
  }

  /**
   * this will handle 2 cases:
   * 1- updating the job, i.e. some information within the job updated
   * 2- changing the job, i.e. the active job actually changed
   *
   * The getJobDetail function, sets the new job automatically. this means both, new job and update job (itself)
   * @param id new job id
   */
  async updateJob(id: string) {
    if (id !== this.currentJob.id) this.updateUrl(id);
    // uses the set function for effects
    await firstValueFrom(
      this.cachedProjectService.getJobDetail(id).pipe(take(1)),
    );
    // this.cdr.markForCheck();
  }

  setParentJobString() {
    let newParentString = "";
    if (this.currentJob.parent_job_id) {
      this.parentJob = this.project.jobs.find(
        (job) => job.id == this.currentJob.parent_job_id,
      );
    } else {
      this.parentJob = undefined;
    }

    if (!this.parentJob) {
      this.parentJob = null;
      newParentString = null;
    } else {
      newParentString = `${this.parentJob.name.substring(0, 10)}`;
      if (
        this.currentJob.parent_cp_num !== null &&
        this.currentJob.parent_cp_num !== undefined
      ) {
        newParentString += ` [CP: ${this.currentJob.parent_cp_num}]`;
      }
      if (this.currentJob.offshoot) {
        newParentString += " [Offshoot]";
      }
    }

    this.parentJobString = newParentString;
  }

  setStorageStatus() {
    this.cleanupTime = null;
    if (!this.project.archiveEnabled) {
      this.storageStatusString = "Archiving not enabled for project";
    } else if (this.currentJob.is_archived) {
      this.storageStatusString = "Deep Archive";
    } else if (this.currentJob.soft_archive_status) {
      this.storageStatusString = "Glacier Instant Retrieval";
    } else if (this.currentJob.keep_job) {
      this.storageStatusString = "Kept";
    } else {
      let created = new Date(this.currentJob.createdDate);
      if (created.getTime() + 7 * 24 * 60 * 60 * 1000 > new Date().getTime()) {
        this.storageStatusString = "< 7 days (Full Storage)";

        let now = new Date();
        let sevendays = 7 * 24 * 60 * 60 * 1000;
        let cleanupdate = new Date(created.getTime() + sevendays);
        let diff = cleanupdate.getTime() - now.getTime();
        let days = 0;
        if (diff > 0) {
          days = Math.floor(diff / (1000 * 60 * 60 * 24));
        }
        this.cleanupTime = "Cleanup in " + days + " days";
      } else {
        this.storageStatusString = "Full Storage";
      }
    }
  }

  toggleKeep() {
    if (this.currentJob.is_archived) {
      this.modalService.info({
        nzTitle: "Glacier Instant Retrival",
        nzContent: `The job has already been send to Amazon Deep Archive. Please contact the administrator if you want to restore the job.`,
      });
    } else if (this.currentJob.soft_archive_status) {
      this.modalService.info({
        nzTitle: "Deep Archive",
        nzContent: `The job has already been send to Amazon Glacier Instant Retrieval. The intermediate checkpoints of the job cannot be recovered, however relevant files and checkpoints are available for viewing.`,
      });
    } else if (this.currentJob.keep_job) {
      this.modalService.confirm({
        nzTitle: `Unkeep ${this.currentJob.name}`,
        nzContent: `Are you sure you'd like to mark this job for archiving?`,
        nzOnOk: () => this.toggleKeepHandler(false),
      });
    } else {
      this.modalService.confirm({
        nzTitle: `Keep ${this.currentJob.name}`,
        nzContent: `Are you sure you'd like to keep this job?`,
        nzOnOk: () => this.toggleKeepHandler(true),
      });
    }
  }

  onSynchroniseChange(value: boolean) {
    this.storedSettingsService.setSynchroniseSettings(value);
  }

  /**
   *
   * @param job_id
   * @param keep_job
   * @description update the keep_job field in the database
   */
  async toggleKeepHandler(status: boolean) {
    let jobDetailDialog: JobDetailDialog = {
      ...this.currentJob,
      keep_job: status,
    };
    this.cachedProjectService
      .updateJobDetails(jobDetailDialog)
      .toPromise()
      .then(() => {
        this.updateJob(this.currentJob.id);
      })
      .catch((err) => {
        this.notificationService.error(
          "error",
          err?.error?.message ||
            err?.error?.detail ||
            err?.message ||
            "An unexpected error occurred.",
        );
      });
  }

  debouncedIndexChangeHandler(index: number, type: "all" | "fav") {
    this.debouncedIndexChangeSubject.next({ index, type });
  }

  onIndexChangedHandler(index: number, type: "all" | "fav") {
    console.log(index)
    const list = type === "fav" ? this.favJobs : this.jobs;

    // these conditions are pretty much ONLY for the switching with arrow keys scenario
    // this ensures the circular path on the jobs nav
    if (index === -1) index = list.length - 1;
    else if (index === list.length) index = 0;

    let newJob = list[index];
    if (newJob.id != this.currentJob.id) this.updateJob(list[index].id);
  }

  /**
   * a job (currentJob) was deleted from one of the child pages (e.g. runfile, files).
   * remove it locally and land on the job that took its place, wrapping same as arrow-key nav
   */
  onJobDeletedHandler(deletedJobId: string) {
    const index = this.jobs.findIndex((j) => j.id === deletedJobId);

    this.jobs = this.jobs.filter((j) => j.id !== deletedJobId);
    this.project.jobs = this.project.jobs.filter((j) => j.id !== deletedJobId);
    this.favJobsService.remove(deletedJobId);

    if (this.jobs.length === 0) {
      this.router.navigate(["/projects"]);
      return;
    }
    if (index === -1) return;

    this.onIndexChangedHandler(index, "all");
  }

  selectChangeHandler(job: Job) {
    this.updateJob(job.id);
  }

  onGroupClick(group: GroupInterface){
    let redirection_url = `projects/${group.jobs[0].job__id}/model?${group.jobs
      .map((job) => `jobs=${job.job__id}`)
      .join("&")}`;
    window.open(redirection_url, "_self");
  }

  ngOnDestroy() {
    // disconnect observers, prevent memory leak
    if (this.observer) this.observer.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();

    this.destroy$.next();
    this.destroy$.complete();

    this.titleService.setTitle("S-Cube Cloud");
  }

  addToFav() {
    this.favJobsService.add({
      ...this.currentJob,
      name: this.currentJob.name.substring(0, 7),
    });
  }

  removeFromFav() {
    this.favJobsService.remove(this.currentJob.id);
  }

  shiftLeft() {
    this.favJobsService.shift(this.currentJob.id, -1);
  }

  shiftRight() {
    this.favJobsService.shift(this.currentJob.id, 1);
  }

  trackByJobId(index: number, item: any) {
    return item?.id;
  }

  // helper function only for checking detection cycles
  get checkDetection() {
    console.count("Angular Change Detection Cycle");
    return null;
  }

  editProject() {
    this.modalService
      .create({
        nzContent: EditProjectDialogComponent,
        nzData: {
          project: this.project,
        },
      })
      .afterClose.pipe(take(1))
      .subscribe((updated) => {
        if (updated) {
          this.refreshProjectAndJob();
        }
      });
  }

  addJob() {
    this.modalService
      .create({
        nzContent: AddJobDialogComponent,
        nzWidth: 800,
        nzFooter: null,
        nzData: {
          projectId: this.project.id,
          projectName: this.currentJob.projectName,
        },
      })
      .afterClose.pipe(take(1))
      .subscribe((added) => {
        if (added) {
          this.refreshProjectAndJob();
        }
      });
  }

  submitNewJob() {
    window.open(`/project/${this.project.id}/job-submission`, "_blank");
  }

  editJob() {
    this.modalService
      .create({
        nzContent: EditJobDialogComponent,
        nzData: {
          jobId: this.currentJob.id,
        },
      })
      .afterClose.pipe(take(1))
      .subscribe((updated) => {
        if (!updated) {
          return;
        } else {
          this.refreshProjectAndJob();
        }
      });
  }

  compareFn = (o1: any, o2: any): boolean =>
    o1 && o2 ? o1.id === o2.id : o1 === o2;

  private setupObserver(el: HTMLElement) {
    // Disconnect existing observer if it exists
    if (this.observer) this.observer.disconnect();
    this.observer = new IntersectionObserver(
      ([entry]) => {
        // We want the tab bar visible when the anchor is NOT intersecting
        // AND it has scrolled past the top.
        this.bottomTabsVisible =
          !entry.isIntersecting && entry.boundingClientRect.top < 0;
        // this.cdr.markForCheck();
      },
      { threshold: [0] },
    );

    this.observer.observe(el);
  }
  private setupResizeObserver(el: HTMLElement) {
    if (this.resizeObserver) this.resizeObserver.disconnect();

    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        // get component height
        const newHeight = entry.contentRect.height;
        // fund the slider bar
        let slider =
          this.elementRef.nativeElement.querySelector(".sliders-bar");
        if (slider) {
          slider.style.bottom = `${newHeight - 2}px`;
        }
      }
    });
    this.resizeObserver.observe(el);
  }
}
